// src/pages/DeviceManagement/components/FilterPanel/index.jsx
// Main FilterPanel container component
// Part of Device Management Overhaul - Phase 1

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Badge,
  Tooltip,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  FilterList as FilterIcon,
  ExpandLess,
  ExpandMore,
  SaveAlt as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Import sub-components
import QuickFilters from './QuickFilters';
import AdvancedFilters from './AdvancedFilters';
import ActiveFilters from './ActiveFilters';
import SavedFilters from './SavedFilters';

const FilterPanel = ({
  filters,
  onFilterChange,
  locations,
  deviceTypes,
  deviceCounts,
  onRefresh,
  isLoading = false,
  showSavedFilters = true
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Calculate active filter count
  useEffect(() => {
    let count = 0;

    // Count quick filters
    if (filters.quickFilters?.size > 0) {
      count += filters.quickFilters.size;
    }

    // Count search
    if (filters.search) count++;

    // Count advanced filters
    if (filters.locationId?.length > 0) count += filters.locationId.length;
    if (filters.deviceTypeId?.length > 0) count += filters.deviceTypeId.length;
    if (filters.status?.length > 0) count += filters.status.length;
    if (filters.lastSeenAfter) count++;
    if (filters.lastSeenBefore) count++;
    if (filters.unlinked) count++;

    setActiveFilterCount(count);
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        position: 'relative'
      }}
    >
      {/* Loading indicator */}
      {isLoading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          backgroundColor: 'primary.dark',
          color: 'primary.contrastText',
          cursor: 'pointer'
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={activeFilterCount} color="error">
            <FilterIcon />
          </Badge>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Device Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${activeFilterCount} active`}
              size="small"
              sx={{
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                fontWeight: 600
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Refresh button */}
          {onRefresh && (
            <Tooltip title="Refresh device list">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                disabled={isLoading}
                sx={{ color: 'primary.contrastText' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Expand/Collapse button */}
          <IconButton
            size="small"
            sx={{ color: 'primary.contrastText' }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {/* Filter Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          {/* Active Filters Display */}
          {hasActiveFilters && (
            <ActiveFilters
              filters={filters}
              onFilterChange={onFilterChange}
              locations={locations}
              deviceTypes={deviceTypes}
            />
          )}

          {/* Saved Filters */}
          {showSavedFilters && (
            <>
              <SavedFilters
                filters={filters}
                onFilterChange={onFilterChange}
                currentFilterCount={activeFilterCount}
              />
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Quick Filters */}
          <QuickFilters
            filters={filters}
            onFilterChange={onFilterChange}
            deviceCounts={deviceCounts}
            isLoading={isLoading}
          />

          <Divider sx={{ my: 2 }} />

          {/* Advanced Filters */}
          <AdvancedFilters
            filters={filters}
            onFilterChange={onFilterChange}
            locations={locations}
            deviceTypes={deviceTypes}
            isLoading={isLoading}
          />
        </Box>
      </Collapse>

      {/* Collapsed State Summary */}
      {!isExpanded && hasActiveFilters && (
        <Box
          sx={{
            p: 1,
            backgroundColor: 'action.hover',
            borderTop: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="caption" color="textSecondary">
            {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
            {filters.search && ` • Searching for "${filters.search}"`}
            {filters.quickFilters?.has('offline') && ' • Showing offline devices'}
            {filters.quickFilters?.has('unlinked') && ' • Showing unlinked devices'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FilterPanel;